import { LoggingService } from "../loggingSvc";
import { Prerequisite } from "../loading/prerequisite-loading-service";
import { HttpTrackingConfig } from "../../../ShellCommon/logger/http-interceptor";
import { SdControllerUtilities } from "../sd-controller/sd-controller-utilities";
import { ServiceDeskConfig } from "../../Startup/serviceDeskConfig";

/** Service which provides AuthZ information to the client runtime. */
export interface AuthorizationService {
  /**
   * This method checks whether or not the given right name is applicable from the target application.
   *
   * @param rightName The name of the right we are checking presence of.
   * @param applicationId The id of the application we expect this right to be present on. Optional parameter, when left undefined will default to the ServiceDesk appId.
   */
  hasRight(rightName: string, applicationId?: number): boolean;

  /**
   * This method kicks off a fetch for rights, returning a promise which completes when the fetch has completed.
   *
   * @param refresh Whether or not we should force a refresh of rights if they have already been loaded.
   */
  loadRights(refresh?: boolean): ng.IPromise<void>;

  /**
   * This method checks if you have any rights associated.
   */
  hasAnyRight(): boolean;

  /**
   * This method checks if the service is ready
   */
  whenReady(): ng.IPromise<void>;

  /**
   * Return list of BG group under current application.
   */
  getBusinessGroupList():Array<string>;

   /**
   * Check rightname id businessGroup is exist or not
   */
  hasRightByBusinessGroup(businessGroup:string, rightName: string): boolean
}

export interface AuthorizationContractApp {
  applicationId: number;
  roleName?: string;
  rights: AuthorizationContractRight[];
  namespace?: string;
}

export interface AuthorizationContractRight {
  name: string;
}

export const rightsUrl = "customer/api/access/rights";

export class AuthorizationServiceImpl implements AuthorizationService {
  private authorizationFetch: ng.IPromise<any>;
  private lastFetchedPermissionMap: Map<number, Set<string>> = new Map<number, Set<string>>();
  private lastFetchedBusinessGroupMap: Map<number, Set<string>> = new Map<number, Set<string>>();
  private lastFetchedBGAndRightMap: Map<string, Set<string>> = new Map<string, Set<string>>();
  private readyDeferral: ng.IDeferred<any>;

  // @ngInject
  constructor(
    private $http: ng.IHttpService,
    private loggingService: LoggingService,
    private $q: ng.IQService,
    private sdControllerUtilities: SdControllerUtilities,
    private sdConfig: ServiceDeskConfig
  ) {
    this.readyDeferral = $q.defer();
  }

  prerequisiteLoadCallback(prerequisite: Prerequisite): ng.IPromise<any> {
    return this.loadInitialRights(prerequisite).then((result) => {
      this.readyDeferral.resolve();
      return result;
    });
  }

  private loadInitialRights(prerequisite: Prerequisite): ng.IPromise<any> {
    if (!prerequisite.authorization) {
      return this.$q.resolve();
    }
    if (
      !angular.isArray(prerequisite.authorization) ||
      prerequisite.authorization.find((bool) => {
        return typeof bool != "boolean";
      })
    ) {
      throw Error(`malformed prerequisite for authorization. ${prerequisite}`);
    }
    if (prerequisite.authorization.includes(true)) {
      // Until we're sure this works as expected in PROD, leave it optional.
      return this.loadRights().catch((error) => {
        this.loggingService.logError(error);
        return this.$q.resolve();
      });
    }
    return this.$q.resolve();
  }

  whenReady(): ng.IPromise<void> {
    return this.readyDeferral.promise;
  }

  hasAnyRight(): boolean {
    let hasRight = false;
    this.lastFetchedPermissionMap.forEach((rights) => {
      if (rights.size > 0) {
        hasRight = true;
      }
    });
    return hasRight;
  }

  hasRight(rightName: string, applicationId?: number): boolean {
    // This code currently expects to be loaded before having hasRight called, which is why it is not async.
    let rights = this.lastFetchedPermissionMap.get(applicationId || this.sdConfig.sdAuthorizationAppId);
    if (rights) {
      return rights.has(rightName);
    }

    return false;
  }

  hasRightByBusinessGroup(businessGroup:string, rightName: string): boolean {
    let rights = this.lastFetchedBGAndRightMap.get(businessGroup);
    if (rights) {
      return rights.has(rightName);
    }
    return false;
  }

  getBusinessGroupList():Array<string> {
    return Array.from(this.lastFetchedBusinessGroupMap.get( this.sdConfig.sdAuthorizationAppId) || []);
  }

  loadRights(refresh = false): ng.IPromise<void> {
    let config: HttpTrackingConfig = this.sdControllerUtilities.getParametersForUserCacheBusting();
    config.dependencyOperationName = "Access.GetUserRights";

    config.qosEventCallback = (result: any) => {
      let roles = "";
      let data = <AuthorizationContractApp[]>result.data;
      if (data && Array.isArray(data)) {
        roles = data.map((app) => app.roleName).join(",");
      }
      return {
        pageTags: { roles: roles }
      };
    };

    if (refresh || !this.authorizationFetch) {
      this.authorizationFetch = this.$http.get(this.sdConfig.sdBaseUrl + rightsUrl, config).then(
        (successResult) => {
          this.lastFetchedPermissionMap = new Map<number, Set<string>>();
          let data = <AuthorizationContractApp[]>successResult.data;
          if (data && Array.isArray(data)) {
            data.forEach((app) => {
              let appSet = this.lastFetchedPermissionMap.get(app.applicationId);
              let appBusinessGroup = this.lastFetchedBusinessGroupMap.get(app.applicationId);
              let rightsSetForBG = this.lastFetchedBGAndRightMap.get(app.namespace);
              if (!appSet) {
                appSet = new Set<string>();
                appBusinessGroup = new Set<string>();
                this.lastFetchedPermissionMap.set(app.applicationId, appSet);
                this.lastFetchedBusinessGroupMap.set(app.applicationId, appBusinessGroup);
              }

              if(!rightsSetForBG){
                rightsSetForBG = new Set<string>();
                this.lastFetchedBGAndRightMap.set(app.namespace, rightsSetForBG)
              }

              appBusinessGroup.add(app.namespace);

              let rights = app.rights;
              if (rights && Array.isArray(rights)) {
                rights.forEach((right: AuthorizationContractRight) => {
                  if (right.name) {
                    appSet.add(right.name);
                    rightsSetForBG.add(right.name);
                  }
                });
              }
            });
          }

          return this.lastFetchedPermissionMap;
        },
        (failureResult) => {
          this.loggingService.logError("Authorization fetch failed, result: " + JSON.stringify(failureResult));
          return this.$q.reject("User rights failed to load.");
        }
      );
    }

    return this.authorizationFetch;
  }
}
